import { useNavigate } from 'react-router-dom';
import { TIER_PRICING, type Tier } from '@mockd/shared';
import { useAuth, useAccountUsage } from '../hooks';
import { Loading } from '../components/common';
import { ROUTES } from '../routes';

const tierLabel = (tier: string) => tier.charAt(0).toUpperCase() + tier.slice(1);

const tierBadgeClass = (tier: string) => {
  switch (tier) {
    case 'pro':
      return 'badge-secondary';
    case 'business':
      return 'badge-primary';
    default:
      return 'badge-ghost';
  }
};

const tierPrice = (tier: string) =>
  `$${(TIER_PRICING[tier as Tier] ?? TIER_PRICING.free).monthly}/month`;

export const Billing: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { usage, loading: usageLoading } = useAccountUsage();

  const tier = user?.tier || 'free';
  const isPro = tier === 'pro' || tier === 'business';

  return (
    <div className="flex-1 bg-base-200 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <h1>Billing</h1>

        {/* Current Plan */}
        <div className="card bg-base-100">
          <div className="card-body">
            <div className="flex justify-between items-center">
              <h2 className="card-title text-lg">Current Plan</h2>
              <span className={`badge ${tierBadgeClass(tier)}`}>
                {tierLabel(tier)}
              </span>
            </div>
            <p className="text-2xl font-bold mt-2">{tierPrice(tier)}</p>
            {usageLoading ? (
              <Loading className="py-4" />
            ) : usage ? (
              <div className="grid grid-cols-2 gap-2 mt-4">
                <div>
                  <span className="text-xs text-base-content/70">Projects</span>
                  <p className="text-sm font-medium">{usage.limits.projects}</p>
                </div>
                <div>
                  <span className="text-xs text-base-content/70">Endpoints per project</span>
                  <p className="text-sm font-medium">{usage.limits.endpointsPerProject}</p>
                </div>
                <div>
                  <span className="text-xs text-base-content/70">Requests per day</span>
                  <p className="text-sm font-medium">{usage.limits.requestsPerDay.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-xs text-base-content/70">Log retention</span>
                  <p className="text-sm font-medium">{usage.limits.logRetentionDays} day{usage.limits.logRetentionDays !== 1 ? 's' : ''}</p>
                </div>
              </div>
            ) : null}
            <div className="mt-4">
              {isPro ? (
                <div className="flex items-center gap-4">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate(ROUTES.pricing())}
                  >
                    Change Plan
                  </button>
                  <span className="text-sm text-base-content/70">Next billing date: —</span>
                </div>
              ) : (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate(ROUTES.pricing())}
                >
                  Upgrade to Pro
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Payment Method */}
        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Payment Method</h2>
            <p className="text-sm text-base-content/70 mt-2">No payment method on file.</p>
            <div className="mt-4">
              <button className="btn btn-sm btn-disabled" disabled>
                Add Payment Method
              </button>
            </div>
          </div>
        </div>

        {/* Billing History */}
        <div className="card bg-base-100">
          <div className="card-body">
            <h2 className="card-title text-lg">Billing History</h2>
            <div className="overflow-x-auto mt-2">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={4} className="text-center text-base-content/70">
                      No invoices yet.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
